import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { Flame, Minus, Plus, Leaf, ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const WEIGHTS = ["250g", "500g", "1kg"];

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weight, setWeight] = useState("500g");
  const [qty, setQty] = useState(1);
  const { addItem, openCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-24">Loading...</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center">
      <p className="font-serif text-2xl mb-4">Pickle not found</p>
      <Link to="/shop" className="text-[#9E3B21] font-semibold">Back to shop</Link>
    </div>
  );

  const handleAdd = () => {
    addItem(product, weight, qty);
    toast.success(`${product.name} (${weight}) × ${qty} added to cart`);
    openCart();
  };

  const handleBuyNow = () => {
    addItem(product, weight, qty);
    navigate("/checkout");
  };

  return (
    <div className="fade-up max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/shop" data-testid="pd-back" className="inline-flex items-center gap-1 text-sm text-[#6E675F] hover:text-[#9E3B21] mb-6">
        <ArrowLeft className="w-4 h-4" /> All Pickles
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-[#F4EFE6] relative">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          {product.tag && (
            <Badge className="absolute top-4 left-4 bg-[#9E3B21] text-[#FBF8F3] hover:bg-[#9E3B21] rounded-full px-3 py-1 text-[10px] tracking-wider uppercase">
              {product.tag}
            </Badge>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9E3B21] mb-3">{product.subtitle}</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-[#1E1B18] leading-tight" data-testid="pd-name">{product.name}</h1>

          <div className="flex flex-wrap gap-2 mt-4">
            {product.spiciness && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#9E3B21]/10 text-[#9E3B21] font-medium">
                <Flame className="w-3.5 h-3.5" /> {product.spiciness}
              </span>
            )}
            {product.oil_type && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#5F7355]/10 text-[#5F7355] font-medium">
                <Leaf className="w-3.5 h-3.5" /> {product.oil_type}
              </span>
            )}
          </div>

          <p className="mt-6 text-[#4A423C] leading-relaxed">{product.description}</p>

          {/* Weight selector */}
          <div className="mt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6E675F] mb-3">Choose weight</p>
            <div className="flex gap-2">
              {WEIGHTS.map((w) => (
                <button
                  key={w}
                  data-testid={`pd-weight-${w}`}
                  onClick={() => setWeight(w)}
                  className={`flex-1 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                    weight === w
                      ? "border-[#9E3B21] bg-[#9E3B21]/5 text-[#9E3B21]"
                      : "border-[#E6DEC3] hover:border-[#D9822B]"
                  }`}
                >
                  <div>{w}</div>
                  <div className="text-xs font-medium text-[#6E675F] mt-0.5">{formatINR(product.prices[w])}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div className="mt-6 flex items-center gap-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6E675F]">Qty</p>
            <div className="flex items-center border border-[#E6DEC3] rounded-full">
              <button data-testid="pd-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-[#F4EFE6] rounded-l-full">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-4 font-semibold" data-testid="pd-qty">{qty}</span>
              <button data-testid="pd-qty-inc" onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-[#F4EFE6] rounded-r-full">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="pd-add-to-cart"
              onClick={handleAdd}
              className="btn-primary rounded-full px-8 py-6 text-base flex-1 min-w-[200px]"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Add to Cart · {formatINR(product.prices[weight] * qty)}
            </Button>
            <Button
              data-testid="pd-buy-now"
              onClick={handleBuyNow}
              variant="outline"
              className="rounded-full px-8 py-6 text-base border-[#1E1B18] hover:bg-[#1E1B18] hover:text-[#FBF8F3]"
            >
              Buy Now
            </Button>
          </div>

          {product.ingredients?.length ? (
            <div className="mt-10 pt-8 border-t border-[#E6DEC3]">
              <h3 className="font-serif text-xl font-semibold mb-3">Ingredients</h3>
              <div className="flex flex-wrap gap-2">
                {product.ingredients.map((ing) => (
                  <span key={ing} className="text-xs px-3 py-1.5 rounded-full bg-[#F4EFE6] text-[#4A423C]">{ing}</span>
                ))}
              </div>
              <p className="text-xs text-[#6E675F] mt-4">Shelf life: 12 months. Store in a cool dry place, use a clean dry spoon.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
